import { Component, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { parse } from 'papaparse';
import { PortfolioComponent } from 'src/app/models/portfolio-component.model';
import { MatHorizontalStepper } from '@angular/material/stepper';
import { Router } from '@angular/router';
import { ListsService } from 'src/app/services/lists.service';
import { Currency } from 'src/app/models/currency.model';
import { InvestmentZone } from 'src/app/models/investment-zone.model';
import { Benchmark } from 'src/app/models/benchmark.model';
import { Product } from 'src/app/models/product.model';
import { Frequency } from 'src/app/models/frequency.model';
import { AssetType } from 'src/app/models/asset-type.model';
import { MarketPositions } from 'src/app/models/market-positions.model';
import { OriginalPortfolio } from 'src/app/models/original-portfolio.model';
import { ClustersService } from 'src/app/services/clusters.service';
import { NotificationsService } from 'src/app/services/notifications.service';

export class ManagedComponent {
  ignore: boolean;
  useAsPortfolio: boolean;
  component: PortfolioComponent;
}

@Component({
  selector: 'app-my-portfolio-create',
  templateUrl: './my-portfolio-create.component.html',
  styleUrls: ['./my-portfolio-create.component.scss']
})
export class MyPortfolioCreateComponent implements OnInit {

  @ViewChild(MatHorizontalStepper) stepper: MatHorizontalStepper;
  compositionCompleted: boolean = false;

  selectedBenchmarkId: number = 0;
  selectedFundId: number = 0;

  disableNextSelectFunds: boolean = true;
  disableNextComposition: boolean = true;
  disableNextPortfolio: boolean = true;
  disableNextComponents: boolean = true;

  stepPortfolio: boolean = false;
  stepComponents: boolean = false;
  stepUpload: boolean = false;

  panelOpenState: boolean = false;
  disableActions: boolean = false;

  step: string = 'select-funds';

  selectedPortfolio: ManagedComponent;
  newPortfolio: OriginalPortfolio = {
    investment_zone: undefined,
    currency: undefined,
    market_positions: undefined,
    frequency: undefined,
    asset_type: undefined,
    ticker: 'Unknown',
    benchmark: undefined,
    components: undefined,
    aum: 1000000,
    is_standalone: false,
    is_private: true,
    is_backtest: true
  } as OriginalPortfolio;

  currencyList: Currency[] = [];
  investmentZoneList: InvestmentZone[] = [];
  benchmarkList:Benchmark[] = [];
  productList: Product[] = [];
  frequencyList: Frequency[] = [];
  marketPositionsList: MarketPositions[] = [];
  assetTypeList: AssetType[] = [];

  allBenchmarks: Benchmark[] = [];
  uploadedBenchmarks: Benchmark[] = [];
  benchmarkForm = new FormControl();
  benchmark: Benchmark;

  uploadedComponents: PortfolioComponent[] = [];
  componentsForm = new FormControl();

  managedComponents: ManagedComponent[] = [];
  selectedComponents: PortfolioComponent[] = [];

  standaloneCreated: boolean = false;

  constructor(private clustersService: ClustersService,
              private listsService: ListsService,
              private router: Router,
              private notificationsService: NotificationsService) {}

  ngOnInit(): void {
    this.listsService.loadLists(false).then(
      () => {
        this.currencyList = this.listsService.getCurrencyList();
        this.investmentZoneList = this.listsService.getInvestmentZoneList();
        this.benchmarkList = this.listsService.getBenchmarkList();
        this.frequencyList = this.listsService.getFrequencyList();
        this.marketPositionsList = this.listsService.getMarketPositionsList();
        this.assetTypeList = this.listsService.getAssetTypeList();

        this.setNewPortfolioInfos();
        this.benchmarkList.forEach(
          (bench:Benchmark) => {this.allBenchmarks.push(bench);}
        );

        this.checkAlertSelectFundsParameters();
      }
    );
    this.listsService.loadProductList().then(
      () => {
        this.productList = this.listsService.getProductList();
      }
    );
  }

  onBenchmarksChange() {
    this.allBenchmarks = this.uploadedBenchmarks.concat(this.benchmarkList);
    if (this.benchmark!=null) {
      this.managedComponents.forEach(
        (managed) => {
          managed.component.benchmark = this.benchmark;
        }
      );
      this.newPortfolio.benchmark = this.benchmark;
    }

    this.checkAlertSelectFundsParameters();
    this.checkAlertCompositionParameters();
    this.onReset();
  }

  onSelectBenchmark(benchmark: Benchmark) {
    if (this.benchmarkForm.value!=benchmark) {
      this.benchmarkForm.setValue(benchmark);
      this.onBenchmarksChange();
    }
  }

  onComponentsBenchmarksChange() {
    this.checkAlertCompositionParameters();
  }

  onManagedComponentsChange() {
    this.managedComponents = this.componentsForm.value==null ? [] : this.componentsForm.value.map(
      (component: PortfolioComponent) => {
        return {
          ignore: false,
          useAsPortfolio: false,
          component: component
        } as ManagedComponent
      }
    );

    // If only one component, then it is the portfolio
    this.selectedPortfolio = undefined;
    this.setNewPortfolioInfos();
    if (this.managedComponents.length==1) {
      this.onUseAsPortfolio(this.managedComponents[0]);
    }

    this.checkAlertSelectFundsParameters();
    this.onSelectedComponentsChange();
    this.onReset();
  }

  onSelectedComponentsChange() {
    this.selectedComponents = this.managedComponents.filter(
      (managed) => {return !managed.ignore && !managed.useAsPortfolio;}
    ).map(
      (managed) => {
        return managed.component as PortfolioComponent;
      }
    );

    this.checkAlertCompositionParameters();
    this.setSelectedComponentsWeights();
  }

  changeListener(files: FileList, source?: string){
    if (files && files.length > 0) {
      for (let i = 0; i < files.length; i++) {
        this.parseFile(files[i], source);
      }
    }
  }

  parseFile(file: File, source: string) {
    parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result,) => {
        let dataList = result.data;
        // Filter data to keep only elements with valid date
        dataList = dataList.filter(
          (elem) => {
            if (elem.date != undefined) {
              return this.checkDate(elem.date);
            }
            return false;
          }
        ).map(
          (elem) => {
            elem.date = this.formatDate(elem.date);
            return elem;
          }
        )
        for (let key in dataList[0]) { // Iteration over column names
          if(key != 'date' && key != '') {
            if (source == 'components') {
              let fund = new PortfolioComponent(key);
              fund.is_private = true;
              const frequency_alias = fund.initFromParsedCSVData(dataList);
              fund.frequency = this.listsService.getFrequency('alias',frequency_alias);
              fund.currency = this.newPortfolio.currency;
              fund.investment_zone = this.newPortfolio.investment_zone;
              fund.benchmark = this.benchmark!=null ? this.benchmark : undefined;
              this.uploadedComponents.push(fund);
              this.componentsForm.setValue(this.componentsForm.value ? this.componentsForm.value.concat([fund]) : [fund]);
              this.onManagedComponentsChange();
            } else if (source == 'benchmark') {
              let fund = new Benchmark(key);
              fund.is_private = true;
              fund.initFromParsedCSVData(dataList);
              this.uploadedBenchmarks.push(fund);
              this.benchmark = fund;
              this.onBenchmarksChange();
            }
          }
        }
      }
    });
  }

  formatDate(dateStr: string) {
    let day: string, month: string, year: string;
    if (/^[0-3][0-9]\/[0-1][0-9]\/[0-2][0-9][0-9][0-9]$/.test(dateStr)) { // FORMAT dd/MM/yyyy
      const res = /^([0-3][0-9])\/([0-1][0-9])\/([0-2][0-9][0-9][0-9])$/.exec(dateStr);
      day = res[1]; month = res[2]; year = res[3];
    } else if (/^[0-2][0-9][0-9][0-9]\-[0-1][0-9]\-[0-3][0-9]$/.test(dateStr)) { // FORMAT yyyy-MM-dd
      const res = /^([0-2][0-9][0-9][0-9])\-([0-1][0-9])\-([0-3][0-9])$/.exec(dateStr);
      day = res[3]; month = res[2]; year = res[1];
    } else if (/^[0-3][0-9]\/[0-1][0-9]\/[0-9][0-9]$/.test(dateStr)) { // FORMAT dd/MM/yy
      const res = /^([0-3][0-9])\/([0-1][0-9])\/([0-9][0-9])$/.exec(dateStr);
      day = res[1]; month = res[2]; year = '20'+res[3];
    }
    return year+'-'+month+'-'+day;
  }

  checkDate(dateStr: string) {
    if (/^[0-3][0-9]\/[0-1][0-9]\/[0-2][0-9][0-9][0-9]$/.test(dateStr) || // FORMAT dd/MM/yyyy
        /^[0-2][0-9][0-9][0-9]\-[0-1][0-9]\-[0-3][0-9]$/.test(dateStr) || // FORMAT yyyy-MM-dd
        /^[0-3][0-9]\/[0-1][0-9]\/[0-9][0-9]$/.test(dateStr)) { // FORMAT dd/MM/yy
      return true;
    }
    return false;
  }

  onUseAsPortfolio(managedComponent: ManagedComponent) {
    if (managedComponent==undefined) {
      this.setNewPortfolioInfos();
    }
    if (this.selectedPortfolio===managedComponent || (this.selectedPortfolio!=undefined && managedComponent==undefined)) { // The user wants to unselect the portfolio
      // We check if it can be another component
      if (this.managedComponents.filter(
        (managed) => {return !managed.ignore}
      ).length>1) {
        setTimeout(
          () => {
            this.selectedPortfolio = undefined;
            this.managedComponents.forEach(
              (managed) => {managed.useAsPortfolio = false;}
            );
            this.setNewPortfolioInfos();
            this.onSelectedComponentsChange();
            this.setSelectedComponentsWeights();
          }
        ,100);
      }
    } else if (managedComponent!=undefined) { // The user wants to select a portfolio
      this.selectedPortfolio = managedComponent;
      this.setNewPortfolioInfos(managedComponent.component);
      managedComponent.ignore = false;
      this.managedComponents.forEach(
        (managed) => {managed.useAsPortfolio = false;}
      );
      managedComponent.useAsPortfolio = true;
      // If only one ingrdient not ignored and a portfolio selected then this ingredient is ignored
      if (this.managedComponents.filter(
        (managed) => {return !managed.ignore && !managed.useAsPortfolio}
      ).length==1) {
        this.managedComponents.forEach(
          (managed) => {managed.ignore = !managed.useAsPortfolio;}
        );
      }
      this.onSelectedComponentsChange();
      this.setSelectedComponentsWeights();
    }
  }

  onIgnoreCheck(managedComponent: ManagedComponent, event:any) {
    // If the component is ignored it cannot be the original portfolio
    managedComponent.useAsPortfolio = false;
    if (this.selectedPortfolio===managedComponent || this.selectedPortfolio==undefined) {
      // If only one managed component is not ignored then it is the current original
      let selectedPortfolioTmp:ManagedComponent = undefined;
      for (let i=0; i<this.managedComponents.length; i++) {
        if (!this.managedComponents[i].ignore) {
          if (selectedPortfolioTmp==undefined) {
            selectedPortfolioTmp = this.managedComponents[i];
          } else {
            selectedPortfolioTmp = undefined;
            break;
          }
        }
      }
      this.selectedPortfolio = selectedPortfolioTmp;
      this.onUseAsPortfolio(this.selectedPortfolio);
    } else if (event.checked) {
      // If only one ingredient not ignored and a portfolio selected then this ingredient is ignored
      setTimeout(
        () => {
          if (this.managedComponents.filter(
            (managed) => {return !managed.ignore && managed!==this.selectedPortfolio;}
          ).length==1) {
            this.managedComponents.forEach(
              (managed) => {managed.ignore = !managed.useAsPortfolio;}
            );
          }
        }
      , 100);
    } else {
      // If only one ingredient not ignored then the portfolio is unselected
      if (this.managedComponents.filter(
        (managed) => {return !managed.ignore && managed!=managedComponent;}
      ).length==1) {
        this.onUseAsPortfolio(undefined);
        this.selectedPortfolio = undefined;
      }
    }
  }

  setSelectedComponentsWeights() {
    const nbComponents = this.selectedComponents.length;
    if (nbComponents==0) {
      return;
    }

    const equalWeight: number = 100.0/nbComponents;

    // Set selected components weights
    const precision = 0.01;
    let sum_weights = 0;
    this.selectedComponents.forEach(
      (component) => {
        component.weight = precision*Math.floor(equalWeight/precision);
        sum_weights += component.weight;
      }
    );

    // Fix for rounding problems
    for (let i = 0; i<Math.round((100-sum_weights)/precision); i++) {
      this.selectedComponents[i].weight += precision;
    }
  }

  checkAlertSelectFundsParameters() {
    let noManagedComponents = this.managedComponents.length==0; // Check if there is at least one managed component

    let selectFundsAlerts = [];
    if (noManagedComponents) selectFundsAlerts.push('param/one-fund-needed');
    // TODO: Add possibility to hide a specific notification
    //this.notificationsService.notify(...selectFundsAlerts); // TODO: more precise notification

    let haveBenchmark = false;
    if (!noManagedComponents) {
      haveBenchmark = this.checkAlertSelectBenchmarkParameters(); // Check if a benchmark is selected
    }

    this.disableNextSelectFunds = (noManagedComponents || !haveBenchmark);
  }

  checkAlertSelectBenchmarkParameters(): boolean {
    let haveBenchmark = this.benchmark!=null; // Check if a benchmark is selected

    let selectBenchmarkAlerts = [];
    if (!haveBenchmark) selectBenchmarkAlerts.push('param/benchmark-required');
    // TODO: Add possibility to hide a specific notification
    //this.notificationsService.notify(...selectBenchmarkAlerts); // TODO: more precise notification

    return haveBenchmark;
  }

  checkAlertCompositionParameters() {
    let noComponents = true; // Check if there is at least one component not ignored
    let haveBenchmarks = true; // Check if all components have a benchmark
    this.managedComponents.forEach(
      (managed) => {
        if (!managed.ignore) {
          noComponents = false;
        }
        if (!managed.ignore && managed.component.benchmark==undefined) {
          haveBenchmarks = false;
        }
      }
    );

    let compositionAlerts = [];
    if (noComponents) compositionAlerts.push('param/one-component-needed');
    if (!haveBenchmarks) compositionAlerts.push('param/components-benchmarks-required');
    // TODO: Add possibility to hide a specific notification
    //this.notificationsService.notify(...compositionAlerts); // TODO: more precise notification

    this.disableNextComposition = (noComponents || !haveBenchmarks);
  }

  checkAlertPortfolioParameters(): boolean {
    let havePortfolioName = this.newPortfolio.name!=undefined && this.newPortfolio.name!=''; // Check if new portfolio has a name

    let portfolioAlerts = [];
    if (!havePortfolioName) portfolioAlerts.push('param/no-portfolio-name');
    // TODO: Add possibility to hide a specific notification
    //this.notificationsService.notify(...portfolioAlerts); // TODO: more precise notification

    this.disableNextPortfolio = !havePortfolioName;

    return havePortfolioName;
  }

  checkAlertComponentsParameters() {
    let validWeights: boolean = true;
    let sumWeights: number = 0; // the sum of wights must be 100%

    // Checks if valid data were provided in components weights input fields
    this.selectedComponents.forEach(
      (component) => {
        validWeights = validWeights && (component.weight!=null);
      }
    );
    if (!validWeights) {
      // TODO: Add possibility to hide a specific notification
      //this.notificationsService.notify('param/invalid'); // TODO: more precise notification
      this.disableNextComponents = true;
      return;
    }

    // A warning is displayed if the sum of weights is not 100%
    this.selectedComponents.forEach(
      (component) => {
        sumWeights += component.weight;
      }
    );
    const precision = 0.01;
    sumWeights = precision*Math.round(sumWeights/precision);

    let componentsAlerts = [];
    if (sumWeights==0) {
      // If the sum of weights is zero, an error is displayed and next button disabled
      componentsAlerts.push('param/invalid');
      this.disableNextComponents = true;
    } else if (sumWeights!=100) {
      // The sum of weights is not 100%, a warning is dsplayed
      componentsAlerts.push('param/normalization');
      this.disableNextComponents = false;
    }
    // TODO: Add possibility to hide a specific notification
    //this.notificationsService.notify(...componentsAlerts); // TODO: more precise notification
  }

  onReset() {
    if(this.stepper) {
      this.stepper.reset();
    }
    if (this.managedComponents.length && this.benchmark!=null) {
      this.step = 'composition';
    } else {
      this.step = 'select-funds';
    }
  }

  updateStepper() {
    const stepPosition = {
      composition: 0,
      portfolio: 1,
      components: 2,
      upload_cloud: this.selectedComponents.length>1 ? 3 : 2,
      summary: this.selectedComponents.length>1 ? 4 : 3,
    }[this.step]

    this.stepper.reset();
    for (var s = 0; s < stepPosition; s++) {
      this.stepper.next();
    }
  }

  onBack() {
    // Going to previous step
    this.step = {
      composition: 'composition',
      portfolio: 'composition',
      components: 'portfolio',
      upload_cloud: this.selectedComponents.length>1 ? 'components' : 'portfolio',
      summary: 'upload_cloud'
    }[this.step];

    this.updateStepper();
  }

  onNext() {
    // Going to next step
    this.step = {
      composition: 'portfolio',
      portfolio: this.selectedComponents.length>1 ? 'components' : 'upload_cloud',
      components: 'upload_cloud',
      upload_cloud: 'summary',
      summary: 'summary'
    }[this.step]

    this.checkAlertSelectFundsParameters();
    this.checkAlertCompositionParameters();
    this.checkAlertPortfolioParameters();
    this.checkAlertComponentsParameters()

    this.updateStepper();
  }

  setNewPortfolioInfos(product?: Product) {
    if (product==undefined) {
      this.newPortfolio.id = undefined;
      this.newPortfolio.alias = undefined;
      this.newPortfolio.name = undefined;
      this.newPortfolio.frequency = this.listsService.getFrequency('alias','d');
      this.newPortfolio.is_private = true;
      this.newPortfolio.type = undefined;
      this.newPortfolio.currency = this.listsService.getCurrency('alias','USD');
      this.newPortfolio.investment_zone = this.listsService.getInvestmentZone('name','USA');
      this.newPortfolio.market_positions = this.listsService.getMarketPositions('alias','LO');
      this.newPortfolio.asset_type = this.listsService.getAssetType('alias','TR');
      this.newPortfolio.is_backtest = true;
    } else {
      ['id','alias','name','frequency','is_private','type','currency','investment_zone',].forEach(
        (key) => {
          this.newPortfolio[key] = product[key];
        }
      );
      this.newPortfolio.is_backtest = false;
    }
  }

  setNewPortfolioData(product?: Product) {
    ['dates','values','minDate','maxDate','range','historical_values',].forEach(
      (key) => {
        this.newPortfolio[key] = product ? product[key] : undefined;
      }
    )
  }

  async onUpload(is_standalone: boolean) {
    this.notificationsService.runSpinner();
    this.disableActions = true;

    if (!is_standalone) {
      // If a component is selected as the orginal portfolio, its historical data is copied
      if (this.selectedPortfolio!= undefined) {
        this.setNewPortfolioData(this.selectedPortfolio.component);
      } else {
        this.setNewPortfolioData();
      }

      // Add components
      this.newPortfolio.components = this.selectedComponents;

      try {
        // Upload portfolio in the cloud, computes analytics and returns the new cluster ID
        let data = await this.clustersService.createOriginal(this.newPortfolio);

        this.step = 'summary';
        this.notificationsService.notify('portfolio/create-success');
      } catch (e) {
        this.notificationsService.notify('portfolio/create-failed');
      }
    } else {
      const haveBenchmark = this.checkAlertSelectBenchmarkParameters(),
            havePortfolioName = this.checkAlertPortfolioParameters();
      if (haveBenchmark && havePortfolioName) {

        try {
          // Upload portfolio in the cloud, computes analytics and returns the new cluster ID
          let data = await this.clustersService.createStandalone(this.newPortfolio);

          this.notificationsService.notify('standalone/create-success');

          this.standaloneCreated = true;
        } catch (e) {
          this.notificationsService.notify('standalone/create-failed');

          this.standaloneCreated = false;
        }
      }
    }

    this.disableActions = false;
    this.notificationsService.stopSpinner();
  }

  onSeeAnalytics() {
    this.router.navigate(['tools/analytics']);
  }

  onGoToStrategyBuilder() {
    this.router.navigate(['tools/strategy-builder']);
  }

}
